import * as enabledApi from '@shared/api/enabledPlayApi';
import { locales } from '@shared/constants/localization/LocalizationConstants';
import { convertDeviceTypeClientListingToUpdateRequest } from '@shared/hooks/helpers/projectUtils';
import EditDeviceTypeContainer from '@shared/hooks/state/EditDeviceTypeContainer';
import DeviceTypeClientListingModel from '@shared/models/devices/DeviceTypeClientListingModel';
import UpdateDeviceTypeListingsModel from '@shared/models/devices/UpdateDeviceTypeListingsModel';
import { DisplayLocales } from '@shared/models/localization/Locales';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Locale flags for display
const LOCALE_FLAGS: Record<DisplayLocales, string> = {
    en: '🇺🇸',
    es: '🇪🇸',
    fr: '🇫🇷',
    it: '🇮🇹',
    de: '🇩🇪',
    ja: '🇯🇵',
};

type DocumentType = 'metadata' | 'troubleshooting' | 'onboarding' | 'client-listing';

// Map document type IDs to DeviceTypeClientListingModel field names
const DOCUMENT_TYPE_FIELD_MAP: Record<
    DocumentType,
    keyof Pick<
        DeviceTypeClientListingModel,
        'markdownDescription' | 'markdownOnboardingInstructions' | 'markdownTroubleshootingInstructions'
    >
> = {
    metadata: 'markdownDescription',
    troubleshooting: 'markdownTroubleshootingInstructions',
    onboarding: 'markdownOnboardingInstructions',
    'client-listing': 'markdownDescription',
};

export function useDeviceTypeClientListingEditor(organizationId: string | undefined, deviceId: string | undefined) {
    const { t } = useTranslation();
    const { editListing, setEditListing, setListingHasChanges } = EditDeviceTypeContainer.useContainer();
    const [activeDocument, setActiveDocument] = useState<DocumentType>('metadata');
    const [activeLocale, setActiveLocale] = useState<DisplayLocales>('en');
    const [isSaving, setIsSaving] = useState(false);

    const LOCALES = useMemo(
        () =>
            locales.display.map((code) => ({
                code,
                label: t(`common:localization.localesShort.${code}`),
                flag: LOCALE_FLAGS[code],
            })),
        [t]
    );

    // Validation helper for URL fields
    const isValidUrl = (url: string | undefined, isOptional: boolean = false): boolean => {
        if (!url || url.trim() === '') return isOptional;
        return url.startsWith('http://') || url.startsWith('https://');
    };

    // Helper function to check if a listing is complete
    const isListingComplete = (listing: DeviceTypeClientListingModel): boolean => {
        if (!editListing) return false;

        const anyHasOnboarding = editListing.some(
            (l) => l.markdownOnboardingInstructions && l.markdownOnboardingInstructions.length > 1
        );
        const anyHasTroubleshooting = editListing.some(
            (l) => l.markdownTroubleshootingInstructions && l.markdownTroubleshootingInstructions.length > 1
        );

        const requiredFields = [
            listing.title,
            listing.shortDescription,
            isValidUrl(listing.iconUrl),
            isValidUrl(listing.darkModeIconUrl),
            (listing.markdownDescription && listing.markdownDescription.length > 25) ||
                (listing.externalLink && listing.externalLink.trim() !== ''),
        ];

        // Validate optional external link if provided
        if (listing.externalLink && listing.externalLink.trim() !== '') {
            requiredFields.push(isValidUrl(listing.externalLink, true));
        }

        if (anyHasOnboarding) {
            requiredFields.push(
                listing.markdownOnboardingInstructions && listing.markdownOnboardingInstructions.length > 25
            );
        }
        if (anyHasTroubleshooting) {
            requiredFields.push(
                listing.markdownTroubleshootingInstructions && listing.markdownTroubleshootingInstructions.length > 25
            );
        }

        return requiredFields.every(Boolean);
    };

    // Calculate completion percentage for each locale
    const getLocaleCompletion = (localeCode: DisplayLocales): number => {
        if (!editListing) return 0;
        const listing = editListing.find((l) => l.locale === localeCode);
        if (!listing) return 0;

        const anyHasOnboarding = editListing.some(
            (l) => l.markdownOnboardingInstructions && l.markdownOnboardingInstructions.length > 1
        );
        const anyHasTroubleshooting = editListing.some(
            (l) => l.markdownTroubleshootingInstructions && l.markdownTroubleshootingInstructions.length > 1
        );

        let filled = [
            listing.title,
            listing.shortDescription,
            isValidUrl(listing.iconUrl),
            isValidUrl(listing.darkModeIconUrl),
            (listing.markdownDescription && listing.markdownDescription.length > 25) ||
                (listing.externalLink && listing.externalLink.trim() !== ''),
        ].filter(Boolean).length;

        let total = 5;
        if (anyHasOnboarding) {
            total += 1;
            filled +=
                listing.markdownOnboardingInstructions && listing.markdownOnboardingInstructions.length > 25 ? 1 : 0;
        }
        if (anyHasTroubleshooting) {
            total += 1;
            filled +=
                listing.markdownTroubleshootingInstructions && listing.markdownTroubleshootingInstructions.length > 25
                    ? 1
                    : 0;
        }
        return Math.round((filled / total) * 100);
    };

    // Handle content changes for markdown fields
    const handleContentChange = (value: string | undefined) => {
        if (value !== undefined && editListing) {
            const fieldName = DOCUMENT_TYPE_FIELD_MAP[activeDocument];
            const updatedListing = editListing.map((listing) => {
                if (listing.locale === activeLocale) {
                    return {
                        ...listing,
                        [fieldName]: value,
                    };
                }
                return listing;
            });
            setEditListing(updatedListing);
            setListingHasChanges(true);
        }
    };

    // Handle metadata field changes
    const handleMetadataFieldChange = (fieldName: keyof DeviceTypeClientListingModel, value: string) => {
        if (editListing) {
            const updatedListing = editListing.map((listing) => {
                if (listing.locale === activeLocale) {
                    return {
                        ...listing,
                        [fieldName]: value,
                    };
                }
                return listing;
            });
            setEditListing(updatedListing);
            setListingHasChanges(true);
        }
    };

    // Save listings
    const saveListings = async (
        onSuccess: (message: string) => void,
        onError: (message: string) => void
    ): Promise<boolean> => {
        if (!organizationId || !deviceId || !editListing) {
            onError(t('common:saveError'));
            return false;
        }

        // Filter to only complete listings
        const completeListings = editListing.filter(isListingComplete);
        const incompleteListings = editListing.filter((listing) => !isListingComplete(listing));

        if (completeListings.length === 0) {
            onError('All locales are incomplete. Please complete at least one locale to 100% before saving.');
            return false;
        }

        setIsSaving(true);
        try {
            // Create en-us as a copy of en if en exists
            let listingsToSave = [...completeListings];
            const enListing = completeListings.find((l) => l.locale === 'en');
            if (enListing) {
                const hasEnUs = completeListings.some((l) => l.locale === 'en-us');
                if (!hasEnUs) {
                    const enUsListing = { ...enListing, locale: 'en-us' };
                    listingsToSave.push(enUsListing);
                }
            }

            const request: UpdateDeviceTypeListingsModel = {
                listings: convertDeviceTypeClientListingToUpdateRequest(listingsToSave),
            };

            const response = await enabledApi.updateOrgDeviceTypeClientListing(organizationId, deviceId, request);

            if (response.resultType === 'Ok') {
                // Show success message with incomplete locale info
                if (incompleteListings.length > 0) {
                    const enListing = completeListings.find((l) => l.locale === 'en');
                    const hasEnUs = completeListings.some((l) => l.locale === 'en-us');
                    const supportedLocaleCodes = LOCALES.map((loc) => loc.code);
                    const actuallyIncompleteListings = incompleteListings.filter(
                        (l) =>
                            !(l.locale === 'en-us' && enListing && !hasEnUs) &&
                            supportedLocaleCodes.includes(l.locale as DisplayLocales)
                    );

                    if (actuallyIncompleteListings.length > 0) {
                        const incompleteLocales = actuallyIncompleteListings
                            .map((l) => {
                                const localeInfo = LOCALES.find((loc) => loc.code === l.locale);
                                return localeInfo ? `${localeInfo.flag} ${localeInfo.label}` : l.locale;
                            })
                            .join(', ');
                        onSuccess(
                            `Saved ${listingsToSave.length} complete locale(s). ` +
                                `Incomplete locales not saved: ${incompleteLocales}`
                        );
                    } else {
                        onSuccess(`Saved ${listingsToSave.length} locale(s) successfully.`);
                    }
                } else {
                    onSuccess(t('common:saved'));
                }
                setListingHasChanges(false);

                // Reload the listings to get updated data
                const refreshResult = await enabledApi.getOrgDeviceTypeClientListings(organizationId, deviceId);
                if (refreshResult.resultType === 'Ok' && refreshResult.data) {
                    setEditListing(refreshResult.data);
                }
                return true;
            } else {
                const errorMessage = response.errors ? response.errors.join(', ') : '';
                onError(t('common:saveError') + (errorMessage ? ': ' + errorMessage : ''));
                return false;
            }
        } catch (error) {
            console.error('Error saving device type client listings:', error);
            onError(t('common:saveError'));
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    // Get current content for the active document and locale
    const currentContent = useMemo(() => {
        if (!editListing) return '';
        const listing = editListing.find((l) => l.locale === activeLocale);
        if (!listing) return '';
        const fieldName = DOCUMENT_TYPE_FIELD_MAP[activeDocument];
        return listing[fieldName] || '';
    }, [editListing, activeLocale, activeDocument]);

    // Get current listing for metadata fields
    const currentListing = useMemo(() => {
        if (!editListing) return null;
        return editListing.find((l) => l.locale === activeLocale);
    }, [editListing, activeLocale]);

    const isDocumentEmpty = (documentType: DocumentType): boolean => {
        if (!editListing) return true;
        const listing = editListing.find((l) => l.locale === activeLocale);
        if (!listing) return true;
        const fieldName = DOCUMENT_TYPE_FIELD_MAP[documentType];
        const content = listing[fieldName];
        return !content || content.length === 0;
    };
    // Check if a specific document is complete for the current locale
    const isDocumentComplete = (documentType: DocumentType): boolean => {
        if (!editListing || !currentListing) return false;

        // Check if any locale has onboarding or troubleshooting instructions
        const anyHasOnboarding = editListing.some(
            (l) => l.markdownOnboardingInstructions && l.markdownOnboardingInstructions.length > 1
        );
        const anyHasTroubleshooting = editListing.some(
            (l) => l.markdownTroubleshootingInstructions && l.markdownTroubleshootingInstructions.length > 1
        );

        switch (documentType) {
            case 'metadata':
                return !!(
                    currentListing.title &&
                    currentListing.shortDescription &&
                    isValidUrl(currentListing.iconUrl) &&
                    isValidUrl(currentListing.darkModeIconUrl) &&
                    ((currentListing.markdownDescription && currentListing.markdownDescription.length > 25) ||
                        (currentListing.externalLink && currentListing.externalLink.trim() !== ''))
                );
            case 'troubleshooting':
                // Only required if any locale has troubleshooting
                if (!anyHasTroubleshooting) return true;
                return !!(
                    currentListing.markdownTroubleshootingInstructions &&
                    currentListing.markdownTroubleshootingInstructions.length > 25
                );
            case 'onboarding':
                // Only required if any locale has onboarding
                if (!anyHasOnboarding) return true;
                return !!(
                    currentListing.markdownOnboardingInstructions &&
                    currentListing.markdownOnboardingInstructions.length > 25
                );
            case 'client-listing':
                return !!(currentListing.markdownDescription && currentListing.markdownDescription.length > 25);
            default:
                return false;
        }
    };

    return {
        // State
        activeDocument,
        setActiveDocument,
        activeLocale,
        setActiveLocale,
        isSaving,
        LOCALES,

        // Computed values
        currentContent,
        currentListing,

        // Functions
        isValidUrl,
        isListingComplete,
        getLocaleCompletion,
        handleContentChange,
        handleMetadataFieldChange,
        saveListings,
        isDocumentComplete,
        isDocumentEmpty,
    };
}
